import axios from "axios";
import i18n from "i18next";
import { BaseApiUrl } from "../Helpers/ApiHelper";
import { generateAlert } from "../Helpers/AlertMessagesHelper";

// Auth endpoints must NOT trigger the "session expired -> logout + reload" flow.
// A 401 here means bad credentials, not an expired session, so we surface the
// message instead of clearing storage and reloading (which would hide the error).
const AUTH_ENDPOINT_REGEX = /User\/(Login|Register|googleOAuth|HashLogin|passwordresend|confirmemail)/i;

// Recognises the standard backend envelope: { statusCode, data, message }.
const isEnvelope = (body) => body && typeof body === "object" && "statusCode" in body;

export default class HttpClient {
  constructor(baseURL) {
    this.baseURL = BaseApiUrl;
  }

  async makeRequest(method, url, param, data, headers, eAlert, sAlert) {
    const isDebug = window.isDebug || false;
    const isAuthEndpoint = AUTH_ENDPOINT_REGEX.test(url);

    try {
      const response = await axios({
        method,
        url: this.baseURL + url,
        data,
        params: param,
        headers,
      });

      // HTTP 2xx. The body still carries the business status in statusCode.
      if (sAlert && response.data.statusCode === 1) {
        generateAlert("success", i18n.t("requestSuccess"));
      } else if (eAlert && response.data.statusCode !== 1) {
        generateAlert("danger", response.data.message || i18n.t("requestFailed"));
      }

      if (isDebug) {
        console.log("Debug Info:", {
          url,
          responseStatus: response.status,
          businessStatus: response.data.statusCode,
          businessMessage: response.data.message,
          responseData: response.data,
        });
      }

      return response.data;
    } catch (err) {
      const httpStatus = err.response?.status;
      const body = err.response?.data;

      if (isDebug) {
        console.log(err);
        alert(`${httpStatus} - Hata --> ${url} // ${err.name} - ${err.message}`);
      }

      // Session expired / token rejected on a protected endpoint -> force re-login.
      // Skip this for auth endpoints (a 401 there is just wrong credentials).
      if (httpStatus === 401 && !isAuthEndpoint) {
        localStorage.clear();
        window.location.reload(true);
        throw err;
      }

      // Business error (4xx/5xx) that still carries our standard envelope:
      // surface the backend message and return the envelope so existing callers
      // (which branch on statusCode) keep working exactly as before.
      if (isEnvelope(body)) {
        // A refusal because contracts are pending needs no alert: the contract screen shows.
        if (eAlert && !body.data?.contractsPending) generateAlert("danger", body.message || i18n.t("requestFailed"));
        return body;
      }

      // Network error or a non-enveloped failure: nothing meaningful to return.
      if (eAlert) generateAlert("danger", i18n.t("requestFailed"));
      throw err;
    }
  }
}
